import { t, type Language } from "../lib/i18n";
import { RANGE_PRESETS, type RangePreset } from "../lib/range";

interface RangePickerProps {
  selected: RangePreset;
  onSelect: (id: RangePreset) => void;
  language: Language;
}

/** Time-window control — a row of quick presets (Today · 7d · 30d ·
 * 90d · This year · All time). Presentational: the active preset is the
 * parent's state, the days mapping lives in `lib/range.ts`. Rendered once
 * at Dashboard level (below the status strip) so the chosen window applies
 * across every tab. */
export function RangePicker({
  selected,
  onSelect,
  language,
}: RangePickerProps): JSX.Element {
  return (
    <div
      role="group"
      aria-label={t("range.heading", language)}
      className="flex flex-wrap items-center gap-1"
      data-testid="range-picker"
    >
      <span className="mr-1 text-xs font-medium text-kwanza-inkSoft">
        {t("range.heading", language)}:
      </span>
      {RANGE_PRESETS.map(({ id, labelKey }) => {
        const active = selected === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSelect(id)}
            aria-pressed={active}
            data-testid={`range-${id}`}
            className={
              // Navy on mint, never white (design review finding 1): white
              // on `#38BF8B` is 2.3:1; navy is 7.3:1 and is the DS's own
              // prescribed pairing. Inactive chips sit on the card ground
              // with a border rather than on `surface`, where 12px muted
              // text was landing at 4.27:1.
              "rounded px-2.5 py-1 text-xs " +
              (active
                ? "bg-kwanza-accent font-bold text-kwanza-onAccent"
                : "border border-kwanza-divider bg-kwanza-card font-medium text-kwanza-inkSoft hover:text-kwanza-ink")
            }
          >
            {t(labelKey, language)}
          </button>
        );
      })}
    </div>
  );
}
